import { isEmpty } from '@sgme/fp';
import type { BlobWithFilename, DocumentError } from 'apiConnector/document/document';
import { documentApi } from 'apiConnector/document/document';
import classNames from 'classnames';
import { SpDropdownToggle } from 'components/shared/Dropdown';
import { TinySpinner } from 'components/shared/TinySpinner/TinySpinner';
import { useGenerateDocument } from 'hooks/useGenerateDocument';
import { IntlKey } from 'locales';
import { FC, useCallback, useContext, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { tap } from 'rxjs/operators';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';

interface TermsheetButtonProps {
  className?: string;
  locales: ReadonlyArray<string>;
  quoteQuery: unknown;
  productSubtype?: string;
  quoteId?: string;
  quoteState?: string;
  productType?: string;
  onError: (locale: string, error: DocumentError) => void;
  onSuccess: (locale: string) => void;
}
export const TermsheetButton: FC<TermsheetButtonProps> = ({ locales, quoteQuery, className, onError, onSuccess }) => {
  const { formatMessage } = useIntl();
  const [isDownloading, openTermsheet] = useGenerateDocument(
    locale =>
      documentApi
        .generateDocumentV2<BlobWithFilename>(quoteQuery, 'Termsheet', locale)
        .pipe(tap(() => onSuccess(locale))),
    (locale, err) => onError(locale, err),
  );
  const category = useContext(WorkspaceAnalyticCategoryContext);

  const onDownload = useCallback(
    (locale: any) => {
      openTermsheet(locale);
      logAnalytics.trackEvent(category, 'Download termsheet');
    },
    [openTermsheet, category],
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(prevState => !prevState);

  if (isEmpty(locales)) {
    return null;
  }

  if (locales.length > 1) {
    return (
      <Dropdown show={dropdownOpen} onToggle={toggle}>
        <SpDropdownToggle
          caret={false}
          variant="link"
          className="badge rounded-pill badge-lg btn btn-icon-start btn-flat-secondary badge-default text-uppercase ps-1 py-3 pe-1 me-1"
          color=""
        >
          {isDownloading ? <TinySpinner className="m-0 mx-1" /> : <em className="icon mx-1">article</em>}
          <span className="text-primary me-2">TS</span>
          <em className="icon">{dropdownOpen ? 'expand_more' : 'chevron_right'}</em>
        </SpDropdownToggle>
        <DropdownMenu>
          {locales.map(locale => (
            <DropdownItem key={locale} onClick={() => onDownload(locale)}>
              {formatMessage({
                id: `downloadTermsheet.termsheetLanguage.${locale}` as IntlKey,
                defaultMessage: locale,
              })}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  }

  return (
    <button
      className={classNames('btn btn-icon-start btn-flat-secondary text-uppercase ', className)}
      onClick={() => onDownload(locales[0])}
    >
      {isDownloading ? (
        <TinySpinner className="my-auto text-primary" />
      ) : (
        <em className="icon text-primary">article</em>
      )}
      <span className="text-primary">TS</span>
    </button>
  );
};

TermsheetButton.displayName = 'TermsheetButton';
